import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DialogComponent } from "../../../../../shared/components/dialog/dialog.component";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { InputType, DynamicFormControlType, TranslationService, DynamicForm, AppTextbox, CommonService, IChipTextValueOption } from '@waseel-asp/tpa-waseel-lib-staging';
import { API } from "../../../../../shared/constants/api-url";
import { matchControls } from "../../../../../shared/functions/form-comparison";
import { HelperService } from "../../../../../shared/services/common/helper.service";
import { IUser, UserConfig } from "./models/user.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Store } from "@ngxs/store";
import { AuthState } from "../../../../../shared/store/auth/auth.state";
import { Auth } from "../../../../../shared/store/auth/auth.action";
import { getTextboxConfig } from "../../../../../shared/functions/config-function";
import { AuthService } from "../../../../../shared/services/auth/auth.service";
import { ToastrHelperService } from "../../../../../shared/services/common/toast-helper.service";
import { take } from "rxjs";
import { UserType } from "../../../../../shared/constants/user-type-enum";
import { DialogService } from "../../../../../shared/services/dialog/dialog.service";
import { DEFAULT_ROLES_ID, DEFAULT_USERS } from "../../../../../shared/constants/system-default-data-constant";
import { ILoggedInUser } from "../../../../../shared/models/logged-in-user.model";
import { PASSWORD_REGEX, SYSTEM_CONST } from "../../../../../shared/constants/system-constants.model";
import { FormTrackerService } from "../../../../../shared/services/common/form-tracker.service";
import { DataGridFilterCondition } from "../../../../../shared/constants/datagrid-filter-condition.enum";

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private matDialog = inject(MatDialog);
    private toastrHelperService = inject(ToastrHelperService);
    private helperService = inject(HelperService);
    private translate = inject(TranslationService);
    private http = inject(HttpClient);
    private store = inject(Store);
    private authService = inject(AuthService);
    private dialogService = inject(DialogService);
    private commonService = inject(CommonService);
    private formTrackerService = inject(FormTrackerService);

    defaultUserNames = Object.values(DEFAULT_USERS);
    dialogRef: MatDialogRef<any> = null;

    get loggedInUserType(): number | undefined {
        return this.authService.currentUserType();
    }

    get allowedPayerIds(): string[] {
        return this.store.selectSnapshot(AuthState.payerIds) ?? [];
    }

    get allowedProviderIds(): string[] {
        return this.store.selectSnapshot(AuthState.providerIds) ?? [];
    }

    getEditConfig() {
        const config: DynamicForm = { controls: [] };
        config['controls'] = [
            {
                control: getTextboxConfig(this.translate.instant('COMMON.PASSWORD'), 'password', null, InputType.password),
                type: DynamicFormControlType.Text,
            },
            {
                control: getTextboxConfig(this.translate.instant('FORM_FIELD.CONFIRM_PASSWORD'), 'confirmPassword', null, InputType.password),
                type: DynamicFormControlType.Text,
            }
        ];
        return config;
    }

    formGrpForResetPasswprd: FormGroup = new FormGroup({
        password: new FormControl(null, [Validators.required, this.helperService.customPatternValidation(PASSWORD_REGEX, this.translate.instant('COMMON.PASSWORD_VALIDATION'))]),
        confirmPassword: new FormControl(null, [Validators.required]),
    }, { validators: matchControls(this.translate, 'password', 'confirmPassword', this.translate.instant('COMMON.PASSWORD')) });

    openPasswordDialog(id?: string, isLoggedinUser?: boolean) {
        if (!id) return;
        const formControls = this.getEditConfig();
        (formControls.controls[0].control as AppTextbox).suffixIcon = {
            icon: 'visibility_off',
            click: () => this.helperService.changePasswordIconAndFieldType(formControls.controls[0].control as AppTextbox)
        };
        (formControls.controls[1].control as AppTextbox).suffixIcon = {
            icon: 'visibility_off',
            click: () => this.helperService.changePasswordIconAndFieldType(formControls.controls[1].control as AppTextbox)
        };

        const dialogRef = this.matDialog.open(DialogComponent, {
            data: {
                formControls: formControls,
                form: this.formGrpForResetPasswprd,
                title: this.translate.instant('FORM_FIELD.RESET_PASSWORD'),
                dialogButton: {
                    saveButtonConfig: { title: this.translate.instant('FORM_FIELD.SAVE'), showButton: true },
                    cancelButtonConfig: { title: this.translate.instant('COMMON.CANCEL'), showButton: true },
                },
            },
            panelClass: ['primary-modal', 'size-sm'],
        });
        dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
            if (result && result.event == 'save') {
                if (isLoggedinUser)
                    this.alertDialog(result, id);
                else
                    this.updatePassword(result.data.confirmPassword, id);
            }
        });
    }

    alertDialog<T>(result: T, id: string) {
        this.dialogService.confirmActionDialog('COMMON.CHANGE_PASSWORD_CONFIRMATION').subscribe((res) =>
            res && this.updatePassword(result['data'].confirmPassword, id)
        );
    }

    updatePassword(password: string, id: string, isFromCreateUser: boolean = false) {
        const payload: Record<string, string | boolean> = {
            "id": id,
            "value": password,
            "temporary": false
        };
        this.authService.changePassword(payload).subscribe({
            next: () => {
                this.toastrHelperService.showAddEditSuccessMessage(isFromCreateUser ? UserConfig.entityName : UserConfig.PASSWORD, !isFromCreateUser);
                if (this.isLoginUser(id))
                    this.store.dispatch(new Auth.Logout());
            },
            error: () => isFromCreateUser && this.toastrHelperService.showErrorMessage("USER_WITHOUT_CREDENTIALS_ERROR")
        });
    }

    resetPassword(code: string, password: string) {
        const body = {
            code: code,
            newPassword: password
        };
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        this.http.post(API.COMMON.RESET_PASSWORD, body, { headers: headers }).subscribe(() => {
            this.toastrHelperService.showCustomSuccessMessage(UserConfig.PASSWORD, 'COMMON.RESETED');
            this.authService.redirectToLoginPage();
        });
    }

    isLoginUser(userId: string): boolean {
        return this.loggedInUser()?.id == userId;
    }

    loggedInUser(): ILoggedInUser {
        return this.store.selectSnapshot(AuthState.currentUser);
    }

    isDefaultUser(username: string): boolean {
        return this.defaultUserNames.includes(username);
    }

    isDefaultSuperAdminUser(): boolean {
        return DEFAULT_USERS.SUPER_ADMIN == this.loggedInUser()?.username.toLowerCase();
    }

    hasSuperAdminRole(user: IUser): boolean {
        return this.authService.checkIsSuperAdminRole(user);
    }

    isDefaultPayerUser(): boolean {
        return DEFAULT_USERS.PAYER == this.loggedInUser()?.username.toLowerCase();
    }

    isDefaultProviderUser(): boolean {
        return DEFAULT_USERS.PROVIDER == this.loggedInUser()?.username.toLowerCase();
    }

    getUserLabel(user: IUser, isEmailRequired: boolean = true): string {
        if (!user) return '';
        const parts: string[] = [];
        if (user.firstName) parts.push(user.firstName);
        if (user.lastName) parts.push(user.lastName);
        if (user.email && isEmailRequired) parts.push(`(${user.email})`);
        return parts.join(' ');
    }

    changeValidatorsOnUserTypes(providerControl, payerControl, patientControl, currentUserType?: number) {
        if (currentUserType == UserType.TPA) {
            providerControl?.setValidators(null);
            payerControl?.setValidators(null);
            patientControl?.setValidators(null);
        } else if (currentUserType == UserType.Provider) {
            providerControl?.setValidators([Validators.required]);
            payerControl?.setValidators(null);
            patientControl?.setValidators(null);
        } else if (currentUserType == UserType.Payer) {
            payerControl?.setValidators([Validators.required]);
            providerControl?.setValidators(null);
            patientControl?.setValidators(null);
        } else if (currentUserType == UserType.Patient) {
            payerControl?.setValidators(null);
            providerControl?.setValidators(null);
            patientControl?.setValidators([Validators.required]);
        } else {
            providerControl?.setValidators(null);
            payerControl?.setValidators(null);
            patientControl?.setValidators(null);
        }
        providerControl?.updateValueAndValidity();
        payerControl?.updateValueAndValidity();
        patientControl?.updateValueAndValidity();
    }

    logOut() {
        this.formTrackerService.triggerLogout();
        this.store.dispatch(new Auth.Logout(this.store.selectSnapshot(AuthState.isPatientUser)));
    }

    logOutDialog() {
        this.dialogRef = this.commonService.confirmDialodMessage({
            displayOnTop: true,
            title: this.translate.instant(SYSTEM_CONST.CONFIRM),
            message: this.translate.instant('COMMON.LOGOUT_CONFIRMATION'),
            primaryButtonCallback: () => this.logOut(),
        });
    }

    getRoleListPayload(userType?: string) {
        return {
            columns: [
                {
                    excelColumnName: "name",
                    name: "name",
                    searchable: true,
                    orderable: true,
                    filterable: false
                },
                {
                    excelColumnName: SYSTEM_CONST.KEYCLOAK_ATTRIBUTES.USER_TYPE,
                    name: SYSTEM_CONST.KEYCLOAK_ATTRIBUTES.USER_TYPE,
                    searchable: true,
                    filterable: !!userType,
                    filterSearch: userType ? { value: userType, condition: DataGridFilterCondition.IsEqualTo } : null,
                }
            ],
            defaultSortingColumn: "name",
            pageNumber: 0,
            pageSize: 0,
            sortBy: "name",
            sortOrder: "asc"
        };
    }

    isRestricted(mode: 'edit' | 'view' | 'delete', currentUserType?: number, user?: IUser, options?: { isProfilePage?: boolean, userRole?: IChipTextValueOption[], isAdmin?: boolean, isSuperAdmin?: boolean }): boolean {
        if (!user) return false;

        const toStringArray = (value?: string | string[]): string[] =>
            (Array.isArray(value) ? value : [value]).filter((item): item is string => typeof item === 'string' && !!item.trim());

        const getUserAffiliations = (key: 'payers' | 'providers'): string[] => {
            const topLevelValues = toStringArray(user[key]);
            if (topLevelValues.length) return topLevelValues;

            const attributeValues = user.attributes?.[key];
            return toStringArray(Array.isArray(attributeValues) ? attributeValues.filter((item): item is string => typeof item === 'string') : undefined);
        };

        const hasAnyAllowedAffiliation = (userIds: string[], allowedIds: string[]): boolean =>
            !!allowedIds?.length && userIds.some(id => allowedIds.includes(id));

        const isSubset = (ids?: string | string[], allowedList?: string[]) => {
            ids = Array.isArray(ids) ? ids : [ids];
            if (!ids?.length) return false;
            if (!allowedList?.length) return false;
            return (Array.isArray(ids) ? ids : [ids]).some(id => !allowedList.includes(id));
        };

        const userPayers = getUserAffiliations('payers');
        const userProviders = getUserAffiliations('providers');

        const tpaRestricted = this.loggedInUserType === UserType.TPA &&
            (isSubset(userProviders, this.allowedProviderIds) || isSubset(userPayers, this.allowedPayerIds) ||
                (!this.authService.isSuperAdmin() && (mode === 'edit' || mode === 'delete' ? (!!options?.isAdmin || !!options?.isSuperAdmin) : (!options?.userRole?.length && !options?.isProfilePage))));

        const payerRestricted = this.loggedInUserType === UserType.Payer &&
            (currentUserType !== UserType.Payer || !hasAnyAllowedAffiliation(userPayers, this.allowedPayerIds));

        const providerRestricted = this.loggedInUserType === UserType.Provider &&
            (currentUserType !== UserType.Provider || !hasAnyAllowedAffiliation(userProviders, this.allowedProviderIds));

        const patientRestricted = currentUserType === UserType.Patient && !(this.authService.isSuperAdmin() || (this.loggedInUserType === UserType.Patient && options?.isProfilePage));

        return tpaRestricted || payerRestricted || providerRestricted || patientRestricted;
    }


    isGroupRestricted(user: IUser, loginUserRoleId: string): boolean {
        let isPayerRestricted = false;
        let isProviderRestricted = false;
        if (this.loggedInUserType == UserType.Payer) {
            if (loginUserRoleId === DEFAULT_ROLES_ID.PAYER)
                isPayerRestricted = user.groups[0].id === DEFAULT_ROLES_ID.PAYER;
            else
                isPayerRestricted = user.groups[0].id !== loginUserRoleId;
        }
        if (this.loggedInUserType == UserType.Provider) {
            if (loginUserRoleId === DEFAULT_ROLES_ID.PROVIDER)
                isProviderRestricted = user.groups[0].id === DEFAULT_ROLES_ID.PROVIDER;
            else
                isProviderRestricted = user.groups[0].id !== loginUserRoleId;
        }
        return isPayerRestricted || isProviderRestricted;
    }
}
